import React, { lazy } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { openCalendlyPopup } from '../utils/calendly';
import { CheckCircleIcon, TrendingUpIcon, UsersIcon, AwardIcon } from 'lucide-react';
export function AboutPage() {
  const {
    language
  } = useLanguage();
  const content = {
    de: {
      title: 'Über uns',
      subtitle: 'Die Geschichte hinter CRM Kraft',
      founderTitle: 'Gründer & Geschäftsführer',
      heroDescription: 'Mit 16 Jahren mein erstes Gewerbe gegründet. Heute, mit 23 Jahren, helfe ich Unternehmen, ihren Vertrieb durch intelligente CRM-Systeme zu verdoppeln.',
      trustBadges: [{
        icon: UsersIcon,
        text: '1.200+ Vertriebsprofis betreut'
      }, {
        icon: TrendingUpIcon,
        text: '€7M+ zusätzlicher Umsatz generiert'
      }, {
        icon: AwardIcon,
        text: 'Offizieller Close Partner'
      }],
      missionTitle: 'Unsere Mission',
      missionText: 'Wir automatisieren alles außer den Spaß',
      missionDescription: 'Im Vertrieb bedeutet das: Vertriebsteams können sich auf das konzentrieren, was sie am besten können – verkaufen. Keine administrativen Ablenkungen, maximale Vertriebseffizienz und klare Zahlen für Vertriebsleiter.',
      journeyTitle: 'Von der Idee zum Erfolg',
      timeline: [{
        age: '16 Jahre',
        title: 'Der Anfang',
        description: 'Erstes Gewerbe gegründet. Neben der Schule Web-Apps entwickelt mit PHP, JavaScript, CSS und Python. Praktika und Projekte bei einer IT-Firma über eine Bekanntschaft meines Vaters.',
        highlight: 'Erste Schritte in der IT'
      }, {
        age: '17-19 Jahre',
        title: 'Webdesign-Agentur',
        description: 'Vollzeit Webdesign- und Web-App-Entwicklungsagentur aufgebaut. Ein Kunde fragte nach einem CRM-System für seine Leads – das war der Wendepunkt.',
        highlight: 'Der Wendepunkt'
      }, {
        age: '19-23 Jahre',
        title: 'CRM Kraft',
        description: 'Vollzeit-Fokus auf CRM-Systeme. Durch Empfehlungen gewachsen. Heute arbeiten wir mit über 1.200 Vertrieblern zusammen und haben 4,5 Jahre intensive CRM-Erfahrung.',
        highlight: 'Durchbruch & Wachstum'
      }],
      expertiseTitle: 'Einzigartige Kombination: IT + Vertrieb',
      expertiseSubtitle: 'Warum diese Kombination den Unterschied macht',
      expertiseIT: {
        title: 'IT & Entwicklung',
        points: ['Programmierung: PHP, JavaScript, CSS, Python', 'Entwicklung komplexer Webanwendungen', 'CRM-Systeme auf IT-Infrastrukturbasis', 'Professionelle IT-Architektur']
      },
      expertiseSales: {
        title: 'Vertrieb & Praxis',
        points: ['3 Jahre aktive Vertriebserfahrung', 'B2B und B2C Vertriebserfahrung', 'Eigenes Vertriebsteam aufgebaut', 'Erfahrung als Setter und Vertriebsleiter']
      },
      statsTitle: 'Zahlen, die überzeugen',
      stats: [{
        number: '1.200+',
        label: 'Vertriebsprofis betreut',
        description: 'Aktive Zusammenarbeit'
      }, {
        number: '€7M+',
        label: 'Zusätzlicher Umsatz',
        description: 'Für unsere Kunden generiert'
      }, {
        number: '4,5',
        label: 'Jahre CRM-Fokus',
        description: 'Intensive Spezialisierung'
      }, {
        number: '110+',
        label: 'CRM-Systeme',
        description: 'Erfolgreich implementiert'
      }],
      whyDifferentTitle: 'Warum CRM Kraft anders ist',
      whyDifferentPoints: [{
        title: 'Praxiserfahrung aus beiden Welten',
        description: 'Ich komme aus der IT und aus dem Vertrieb. Ich verstehe die technischen Anforderungen und kenne die praktischen Herausforderungen im Vertriebsalltag.'
      }, {
        title: 'Keine Theorie – nur Praxis',
        description: 'Wir entwickeln keine Systeme im Elfenbeinturm. Jede Funktion wurde in der Praxis getestet und optimiert.'
      }, {
        title: 'Persönlicher Support',
        description: 'Du arbeitest direkt mit mir zusammen. Keine anonymen Support-Tickets, sondern echte Partnerschaft.'
      }],
      socialProofTitle: 'Vertrauen von führenden Unternehmen',
      socialProofText: 'Über 110 Unternehmen vertrauen auf CRM Kraft für ihre Vertriebsautomatisierung',
      ctaTitle: 'Bereit, Deinen Vertrieb zu transformieren?',
      ctaSubtitle: 'Lass uns in einem kostenlosen Erstgespräch analysieren, wie wir Dein CRM-System optimieren können.',
      ctaButton: 'Jetzt kostenloses Gespräch buchen',
      ctaBenefits: ['Kostenlose Erstberatung (30 Min)', 'Konkrete Optimierungsvorschläge', 'Keine Verpflichtungen']
    },
    en: {
      title: 'About Us',
      subtitle: 'The Story Behind CRM Kraft',
      founderTitle: 'Founder & CEO',
      heroDescription: 'Founded my first business at 16. Today, at 23, I help companies double their sales through intelligent CRM systems.',
      trustBadges: [{
        icon: UsersIcon,
        text: '1,200+ sales professionals supported'
      }, {
        icon: TrendingUpIcon,
        text: '€7M+ additional revenue generated'
      }, {
        icon: AwardIcon,
        text: 'Official Close Partner'
      }],
      missionTitle: 'Our Mission',
      missionText: 'We automate everything except the fun',
      missionDescription: 'In sales, this means: Sales teams can focus on what they do best – selling. No administrative distractions, maximum sales efficiency, and clear metrics for sales leaders.',
      journeyTitle: 'From Idea to Success',
      timeline: [{
        age: 'Age 16',
        title: 'The Beginning',
        description: "Founded my first business. Developed web apps alongside school using PHP, JavaScript, CSS, and Python. Internships and projects at an IT company through my father's connection.",
        highlight: 'First steps in IT'
      }, {
        age: 'Age 17-19',
        title: 'Web Design Agency',
        description: 'Built a full-time web design and web app development agency. A client asked for a CRM system for their leads – that was the turning point.',
        highlight: 'The turning point'
      }, {
        age: 'Age 19-23',
        title: 'CRM Kraft',
        description: 'Full-time focus on CRM systems. Grew through referrals. Today we work with over 1,200 sales professionals and have 4.5 years of intensive CRM experience.',
        highlight: 'Breakthrough & Growth'
      }],
      expertiseTitle: 'Unique Combination: IT + Sales',
      expertiseSubtitle: 'Why this combination makes the difference',
      expertiseIT: {
        title: 'IT & Development',
        points: ['Programming: PHP, JavaScript, CSS, Python', 'Development of complex web applications', 'CRM systems built on IT infrastructure', 'Professional IT architecture']
      },
      expertiseSales: {
        title: 'Sales & Practice',
        points: ['3 years of active sales experience', 'B2B and B2C sales experience', 'Built my own sales team', 'Experience as setter and sales manager']
      },
      statsTitle: 'Numbers That Convince',
      stats: [{
        number: '1,200+',
        label: 'Sales Professionals',
        description: 'Active collaboration'
      }, {
        number: '€7M+',
        label: 'Additional Revenue',
        description: 'Generated for our clients'
      }, {
        number: '4.5',
        label: 'Years CRM Focus',
        description: 'Intensive specialization'
      }, {
        number: '110+',
        label: 'CRM Systems',
        description: 'Successfully implemented'
      }],
      whyDifferentTitle: 'Why CRM Kraft Is Different',
      whyDifferentPoints: [{
        title: 'Practical experience from both worlds',
        description: 'I come from both IT and sales. I understand the technical requirements and know the practical challenges of daily sales work.'
      }, {
        title: 'No theory – only practice',
        description: "We don't develop systems in an ivory tower. Every feature has been tested and optimized in practice."
      }, {
        title: 'Personal support',
        description: 'You work directly with me. No anonymous support tickets, but real partnership.'
      }],
      socialProofTitle: 'Trusted by Leading Companies',
      socialProofText: 'Over 110 companies trust CRM Kraft for their sales automation',
      ctaTitle: 'Ready to Transform Your Sales?',
      ctaSubtitle: "Let's analyze in a free consultation how we can optimize your CRM system.",
      ctaButton: 'Book Free Consultation Now',
      ctaBenefits: ['Free initial consultation (30 min)', 'Concrete optimization suggestions', 'No obligations']
    }
  };
  const t = content[language];
  // Client logos - optimized 256px versions
  const clientLogos = [{
    url: 'https://cdn.magicpatterns.com/uploads/uMbKRQCRY8Q6qGBFMbab1c/fb_(1).png',
    alt: 'EB Marketing'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/vng5aFMxAmTy35gdqg8Gmu/erk_(1).png',
    alt: 'Emanuel Erk'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/4Mg2QWRXFYtc8z6whSh7dF/byyond_(1).png',
    alt: 'Byynd'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/uZaF7vTaPKxXqXcYe8scE2/Mia_(1)_(1).png',
    alt: 'Mia'
  }, {
    url: 'https://cdn.magicpatterns.com/uploads/pEimFUUHXhRuN33LzRMCPN/TK_(1).png',
    alt: 'TK'
  }];
  return <div className="min-h-screen bg-[#0a0e1a]" data-id="element-296">
      <SEO title={language === 'de' ? 'Über uns - Justus Kraft | CRM Kraft' : 'About Us - Justus Kraft | CRM Kraft'} description={language === 'de' ? 'Erfahre mehr über Justus Kraft, Gründer von CRM Kraft. Mit 16 Jahren das erste Gewerbe gegründet, heute helfe ich Unternehmen, ihren Vertrieb durch intelligente CRM-Systeme zu verdoppeln.' : 'Learn more about Justus Kraft, founder of CRM Kraft. Founded my first business at 16, today I help companies double their sales through intelligent CRM systems.'} canonical="https://crmkraft.de/about" data-id="element-297" />
      <Navigation data-id="element-298" />

      {/* Compact Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1f36] via-[#0a0e1a] to-[#0a0e1a] pt-24 pb-12 px-4" data-id="element-299">
        <div className="max-w-6xl mx-auto" data-id="element-300">
          <div className="grid lg:grid-cols-[1fr,400px] gap-8 items-center" data-id="element-301">
            <div data-id="element-302">
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-303">
                {t.title}
              </h1>
              <p className="text-lg text-gray-400 mb-6" data-id="element-304">{t.subtitle}</p>
              <div className="flex items-center gap-3 mb-6" data-id="element-305">
                <div className="h-1 w-12 bg-[#ff6b35]" data-id="element-306"></div>
                <span className="text-sm font-semibold text-[#ff6b35] uppercase tracking-wide" data-id="element-307">
                  Justus Kraft
                </span>
              </div>
              <p className="text-base text-gray-300 mb-6" data-id="element-308">{t.founderTitle}</p>
              <p className="text-lg text-white mb-8" data-id="element-309">{t.heroDescription}</p>

              {/* Trust Badges */}
              <div className="space-y-3" data-id="element-310">
                {t.trustBadges.map((badge, index) => <div key={index} className="flex items-center gap-3" data-id="element-311">
                    <div className="w-8 h-8 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-312">
                      <badge.icon className="w-4 h-4 text-[#ff6b35]" data-id="element-313" />
                    </div>
                    <span className="text-sm text-gray-300" data-id="element-314">{badge.text}</span>
                  </div>)}
              </div>
            </div>

            <div className="relative max-w-[300px] mx-auto lg:max-w-none" data-id="element-315">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#1a1f36]" data-id="element-316">
                <img srcSet="
                    https://cdn.magicpatterns.com/uploads/mobNPtBM2geWPhf56dFxLz/62dfd10966fa026013ebcbcf_565aa1ce-min-p-1600_(2).jpg 400w,
                    https://cdn.magicpatterns.com/uploads/bB93PFhfhx8KtSt7vvd9UB/62dfd10966fa026013ebcbcf_565aa1ce-min-p-1600_(3).jpg 800w,
                    https://cdn.magicpatterns.com/uploads/sSDXwcgDxXdjdbrta1Zs6Z/62dfd10966fa026013ebcbcf_565aa1ce-min-p-1600_(4).jpg 1200w
                  " sizes="(max-width: 640px) 300px, (max-width: 1024px) 400px, 400px" src="https://cdn.magicpatterns.com/uploads/bB93PFhfhx8KtSt7vvd9UB/62dfd10966fa026013ebcbcf_565aa1ce-min-p-1600_(3).jpg" alt="Justus Kraft - Gründer von CRM Kraft" className="w-full h-auto" loading="eager" fetchpriority="high" width="400" height="600" data-id="element-317" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Moved up for conversion */}
      <section className="py-16 px-4 bg-[#0d1225]" data-id="element-318">
        <div className="max-w-6xl mx-auto" data-id="element-319">
          <h2 className="text-3xl font-bold text-center text-white mb-12" data-id="element-320">
            {t.statsTitle}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-321">
            {t.stats.map((stat, index) => <div key={index} className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10 text-center" data-id="element-322">
                <div className="text-4xl font-bold text-[#ff6b35] mb-2" data-id="element-323">
                  {stat.number}
                </div>
                <div className="text-base font-semibold text-white mb-1" data-id="element-324">
                  {stat.label}
                </div>
                <div className="text-sm text-gray-400" data-id="element-325">{stat.description}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 bg-[#ff6b35]" data-id="element-326">
        <div className="max-w-4xl mx-auto text-center" data-id="element-327">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-328">
            {t.missionTitle}
          </h2>
          <p className="text-3xl lg:text-4xl font-bold mb-6 italic text-white" data-id="element-329">
            "{t.missionText}"
          </p>
          <p className="text-lg text-white/90 leading-relaxed" data-id="element-330">
            {t.missionDescription}
          </p>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="py-16 px-4 bg-[#0a0e1a]" data-id="element-331">
        <div className="max-w-5xl mx-auto" data-id="element-332">
          <h2 className="text-3xl font-bold text-center text-white mb-12" data-id="element-333">
            {t.whyDifferentTitle}
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-334">
            {t.whyDifferentPoints.map((point, index) => <div key={index} className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10" data-id="element-335">
                <div className="w-12 h-12 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-336">
                  <CheckCircleIcon className="w-6 h-6 text-[#ff6b35]" data-id="element-337" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3" data-id="element-338">
                  {point.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed" data-id="element-339">
                  {point.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Journey Timeline - Compact */}
      <section className="py-16 px-4 bg-[#0d1225]" data-id="element-340">
        <div className="max-w-5xl mx-auto" data-id="element-341">
          <h2 className="text-3xl font-bold text-center text-white mb-12" data-id="element-342">
            {t.journeyTitle}
          </h2>
          <div className="space-y-8" data-id="element-343">
            {t.timeline.map((item, index) => <div key={index} className="relative pl-8 border-l-2 border-[#ff6b35]" data-id="element-344">
                <div className="absolute -left-2 top-0 w-4 h-4 bg-[#ff6b35] rounded-full" data-id="element-345"></div>
                <div className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10" data-id="element-346">
                  <div className="flex items-center justify-between mb-3" data-id="element-347">
                    <div className="text-xs font-bold text-[#ff6b35] uppercase tracking-wide" data-id="element-348">
                      {item.age}
                    </div>
                    <div className="text-xs font-semibold text-green-400 bg-green-400/10 px-3 py-1 rounded-full" data-id="element-349">
                      {item.highlight}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-350">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-351">
                    {item.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Dual Expertise - Compact */}
      <section className="py-16 px-4 bg-[#0a0e1a]" data-id="element-352">
        <div className="max-w-5xl mx-auto" data-id="element-353">
          <h2 className="text-3xl font-bold text-center text-white mb-3" data-id="element-354">
            {t.expertiseTitle}
          </h2>
          <p className="text-center text-gray-400 mb-12" data-id="element-355">
            {t.expertiseSubtitle}
          </p>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-356">
            {/* IT Expertise */}
            <div className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-[#ff6b35]/20" data-id="element-357">
              <div className="w-12 h-12 bg-[#ff6b35] rounded-lg flex items-center justify-center mb-4" data-id="element-358">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-359">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" data-id="element-360" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-361">
                {t.expertiseIT.title}
              </h3>
              <ul className="space-y-2" data-id="element-362">
                {t.expertiseIT.points.map((point, index) => <li key={index} className="flex items-start gap-2" data-id="element-363">
                    <CheckCircleIcon className="w-4 h-4 text-[#ff6b35] flex-shrink-0 mt-0.5" data-id="element-364" />
                    <span className="text-sm text-gray-300" data-id="element-365">{point}</span>
                  </li>)}
              </ul>
            </div>

            {/* Sales Expertise */}
            <div className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-green-500/20" data-id="element-366">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4" data-id="element-367">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-368">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-369" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-370">
                {t.expertiseSales.title}
              </h3>
              <ul className="space-y-2" data-id="element-371">
                {t.expertiseSales.points.map((point, index) => <li key={index} className="flex items-start gap-2" data-id="element-372">
                    <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" data-id="element-373" />
                    <span className="text-sm text-gray-300" data-id="element-374">{point}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 px-4 bg-[#0d1225]" data-id="element-375">
        <div className="max-w-4xl mx-auto text-center" data-id="element-376">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-377">
            {t.socialProofTitle}
          </h2>
          <p className="text-lg text-gray-400 mb-12" data-id="element-378">{t.socialProofText}</p>
          <div className="flex flex-wrap justify-center items-center gap-8" data-id="element-379">
            {clientLogos.map((logo, i) => <div key={i} className="h-16 w-32 flex items-center justify-center grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100" data-id="element-380">
                <img src={logo.url} alt={logo.alt} className="max-h-full max-w-full object-contain" loading="lazy" width="128" height="64" data-id="element-381" />
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section - Conversion focused */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#ff6b35] to-[#ff5722]" data-id="element-382">
        <div className="max-w-4xl mx-auto text-center" data-id="element-383">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-384">
            {t.ctaTitle}
          </h2>
          <p className="text-lg text-white/90 mb-8" data-id="element-385">{t.ctaSubtitle}</p>

          {/* Benefits */}
          <div className="flex flex-wrap justify-center gap-6 mb-10" data-id="element-386">
            {t.ctaBenefits.map((benefit, index) => <div key={index} className="flex items-center gap-2 text-white" data-id="element-387">
                <CheckCircleIcon className="w-5 h-5" data-id="element-388" />
                <span className="text-sm font-medium" data-id="element-389">{benefit}</span>
              </div>)}
          </div>

          <button onClick={openCalendlyPopup} className="inline-flex items-center gap-2 bg-white text-[#ff6b35] px-8 py-4 rounded-xl text-lg font-bold hover:bg-gray-100 transition-all hover:scale-105 shadow-2xl" data-id="element-390">
            {t.ctaButton}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-391">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-392" />
            </svg>
          </button>
        </div>
      </section>

      <Footer data-id="element-393" />
    </div>;
}